import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, CloudCog, RefreshCw, Save, Server, Shield, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import {
  api,
  type AdminNode,
  type AutoscalingAction,
  type Session,
  type TencentAsCapacity,
  type TencentAsGroup,
  type TencentAsSettings,
} from "../api";
import { EmptyState, ErrorNotice, LoadingRows, Modal, Status, formatTime } from "../components";

function groupToCapacity(group: TencentAsGroup): TencentAsCapacity {
  return {
    auto_scaling_group_id: group.auto_scaling_group_id,
    auto_scaling_group_name: group.auto_scaling_group_name,
    min_size: group.min_size,
    desired_capacity: group.desired_capacity,
    current_instances: group.in_service_instance_count,
    in_service_instances: group.in_service_instance_count,
    max_size: group.max_size,
    status: group.status,
    in_activity_status: group.in_activity_status,
    instance_ids: [],
  };
}

export function AutoscalingPage({ role }: { role: Session["role"] }) {
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState<string | null>(null);
  const [capacity, setCapacity] = useState<TencentAsCapacity | null>(null);
  const [groups, setGroups] = useState<TencentAsGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [scaleTarget, setScaleTarget] = useState<AdminNode | null>(null);
  const settingsForm = useRef<HTMLFormElement>(null);
  const settings = useQuery({
    queryKey: ["tencent-as-settings"],
    queryFn: () => api<TencentAsSettings>("/admin/v1/tencent-as-settings"),
  });
  const liveCapacity = useQuery({
    queryKey: ["tencent-as-capacity", settings.data?.version],
    queryFn: () => api<TencentAsCapacity>("/admin/v1/tencent-as-settings/validate", { method: "POST" }),
    enabled: Boolean(settings.data?.credentials_configured && settings.data.auto_scaling_group_id),
    refetchInterval: 30_000,
    retry: false,
  });
  const nodes = useQuery({
    queryKey: ["nodes"],
    queryFn: () => api<AdminNode[]>("/admin/v1/nodes"),
    refetchInterval: 10_000,
  });
  const actions = useQuery({
    queryKey: ["autoscaling-actions"],
    queryFn: () => api<AutoscalingAction[]>("/admin/v1/autoscaling-actions"),
    refetchInterval: 5_000,
  });
  const update = useMutation({
    mutationFn: (body: {
      expected_version: number;
      enabled: boolean;
      pool_id: string;
      region: string;
      auto_scaling_group_id: string | null;
      secret_id?: string;
      secret_key?: string;
      scale_out_queue_threshold: number;
      scale_out_sustain_seconds: number;
      scale_out_step: number;
      backlog_jobs_per_node: number;
      scale_in_queue_threshold: number;
      scale_in_sustain_seconds: number;
      cooldown_seconds: number;
      min_nodes: number;
      max_nodes: number;
      node_ready_timeout_seconds: number;
      workers_per_node: number;
      reason: string;
    }) => api<TencentAsSettings>("/admin/v1/tencent-as-settings", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
    onSuccess: async (data) => {
      setFeedback("腾讯云 AS 配置已保存。凭证内容不会在页面中回显。");
      queryClient.setQueryData(["tencent-as-settings"], data);
    },
  });
  const discoverGroups = useMutation({
    mutationFn: (body: { region: string; secret_id?: string; secret_key?: string }) =>
      api<TencentAsGroup[]>("/admin/v1/tencent-as-groups", {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: (data) => {
      setGroups(data);
      const currentGroup = data.find((group) => group.auto_scaling_group_id === selectedGroupId) ?? data[0];
      setSelectedGroupId(currentGroup?.auto_scaling_group_id ?? "");
      setCapacity(currentGroup ? groupToCapacity(currentGroup) : null);
      setFeedback(data.length
        ? `连接成功，发现 ${data.length} 个伸缩组。请选择需要管理的伸缩组。`
        : "连接成功，但该地域没有可用的伸缩组。");
    },
  });
  const scaleIn = useMutation({
    mutationFn: ({ node, reason, confirmation }: { node: AdminNode; reason: string; confirmation: string }) =>
      api<AutoscalingAction>(`/admin/v1/nodes/${node.node_id}/scale-in`, {
        method: "POST",
        body: JSON.stringify({ confirm_instance_id: confirmation, reason }),
      }),
    onSuccess: async (action) => {
      setScaleTarget(null);
      setFeedback(`节点已进入排空，缩容动作 ${action.action_id} 将在任务归零后执行。`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["nodes"] }),
        queryClient.invalidateQueries({ queryKey: ["autoscaling-actions"] }),
      ]);
    },
  });
  const scaleOut = useMutation({
    mutationFn: ({ nodes, reason }: { nodes: number; reason: string }) =>
      api<AutoscalingAction>("/admin/v1/autoscaling-actions/scale-out", {
        method: "POST",
        body: JSON.stringify({ nodes, reason }),
      }),
    onSuccess: async (action) => {
      setFeedback(`手动扩容已提交，期望容量 ${action.desired_capacity_before} → ${action.desired_capacity_target}。`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["autoscaling-actions"] }),
        queryClient.invalidateQueries({ queryKey: ["tencent-as-settings"] }),
      ]);
    },
  });

  useEffect(() => {
    if (settings.data?.auto_scaling_group_id) {
      setSelectedGroupId(settings.data.auto_scaling_group_id);
    }
  }, [settings.data?.auto_scaling_group_id]);

  const testConnection = () => {
    if (!settingsForm.current) return;
    setFeedback(null);
    const data = new FormData(settingsForm.current);
    const secretId = String(data.get("secret_id") ?? "").trim();
    const secretKey = String(data.get("secret_key") ?? "").trim();
    discoverGroups.mutate({
      region: String(data.get("region")).trim(),
      ...(secretId ? { secret_id: secretId } : {}),
      ...(secretKey ? { secret_key: secretKey } : {}),
    });
  };

  const submitSettings = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!settings.data) return;
    setFeedback(null);
    const data = new FormData(event.currentTarget);
    const secretId = String(data.get("secret_id") ?? "").trim();
    const secretKey = String(data.get("secret_key") ?? "").trim();
    update.mutate({
      expected_version: settings.data.version,
      enabled: data.get("enabled") === "on",
      pool_id: String(data.get("pool_id")).trim(),
      region: String(data.get("region")).trim(),
      auto_scaling_group_id: selectedGroupId || null,
      ...(secretId ? { secret_id: secretId } : {}),
      ...(secretKey ? { secret_key: secretKey } : {}),
      scale_out_queue_threshold: Number(data.get("scale_out_queue_threshold")),
      scale_out_sustain_seconds: Number(data.get("scale_out_sustain_seconds")),
      scale_out_step: settings.data.scale_out_step,
      backlog_jobs_per_node: Number(data.get("backlog_jobs_per_node")),
      scale_in_queue_threshold: Number(data.get("scale_in_queue_threshold")),
      scale_in_sustain_seconds: Number(data.get("scale_in_sustain_seconds")),
      cooldown_seconds: Number(data.get("cooldown_seconds")),
      min_nodes: Number(data.get("min_nodes")),
      max_nodes: Number(data.get("max_nodes")),
      node_ready_timeout_seconds: Number(data.get("node_ready_timeout_seconds")),
      workers_per_node: Number(data.get("workers_per_node")),
      reason: String(data.get("reason")).trim(),
    });
  };

  const error = settings.error ?? nodes.error ?? actions.error ?? update.error ?? discoverGroups.error ?? scaleIn.error ?? scaleOut.error ?? liveCapacity.error;
  const liveInstanceIds = new Set(liveCapacity.data?.instance_ids ?? []);
  const poolNodes = nodes.data?.filter((node) => node.pool_id === settings.data?.pool_id && node.provider_instance_id && liveInstanceIds.has(node.provider_instance_id)) ?? [];
  const displayedCapacity = liveCapacity.data ?? capacity;
  const capacityMembershipPending = Boolean(settings.data?.credentials_configured && settings.data.auto_scaling_group_id && liveCapacity.isPending);

  return <div className="page page--autoscaling">
    <header className="page-heading autoscaling-heading"><div><p className="eyebrow">CAPACITY / TENCENT CLOUD AS</p><h1>弹性伸缩</h1><p>维护腾讯云凭证与伸缩组，并对节点执行先排空、再精确缩容的一体化操作。</p></div></header>
    {error && <ErrorNotice error={error} />}
    {feedback && <div className="notice notice--positive" role="status"><CheckCircle2 size={17} />{feedback}</div>}

    {settings.isPending ? <LoadingRows columns={3} /> : settings.data && <>
      <section className="autoscaling-state" aria-label="腾讯云弹性伸缩状态">
        <div className={settings.data.enabled ? "autoscaling-state__mode autoscaling-state__mode--enabled" : "autoscaling-state__mode"}>
          <CloudCog size={20} /><div><span>控制状态</span><strong>{settings.data.enabled ? "已启用" : "已停用"}</strong></div>
        </div>
        <dl>
          <div><dt>资源池</dt><dd>{settings.data.pool_id}</dd></div>
          <div><dt>地域</dt><dd>{settings.data.region}</dd></div>
          <div><dt>伸缩组</dt><dd>{settings.data.auto_scaling_group_id ?? "未配置"}</dd></div>
          <div><dt>每节点 Worker</dt><dd>{settings.data.workers_per_node}</dd></div>
          <div><dt>凭证</dt><dd>{settings.data.credentials_configured ? settings.data.secret_id_hint : "未配置"}</dd></div>
        </dl>
      </section>
      <section className="autoscaling-policy-state" aria-label="自动伸缩策略状态">
        <div><span>最近评估 / 决策</span><strong>{settings.data.policy_last_evaluated_at ? `${formatTime(settings.data.policy_last_evaluated_at)} · ${settings.data.last_decision ?? "无动作"}` : settings.data.last_decision ?? "等待首次评估"}</strong></div>
        <div><span>高负载持续起点</span><strong>{settings.data.above_threshold_since ? formatTime(settings.data.above_threshold_since) : "—"}</strong></div>
        <div><span>低负载持续起点</span><strong>{settings.data.below_threshold_since ? formatTime(settings.data.below_threshold_since) : "—"}</strong></div>
        <div><span>冷却至</span><strong>{settings.data.cooldown_until ? formatTime(settings.data.cooldown_until) : "—"}</strong></div>
        {settings.data.policy_last_error && <p><AlertTriangle size={15} />{settings.data.policy_last_error}</p>}
      </section>

      <div className="autoscaling-layout">
        <section className="autoscaling-editor">
          <div className="autoscaling-section-copy"><p className="eyebrow">PROVIDER SETTINGS</p><h2>腾讯云连接</h2><p>先验证地域与凭证，成功后从腾讯云实时读取伸缩组，再选择需要管理的组。SecretKey 不会在页面、查询接口或审计记录中回显。</p></div>
          {role === "ADMIN" ? <form ref={settingsForm} className="autoscaling-form" key={settings.data.version} onSubmit={submitSettings}>
            <div className="autoscaling-form__fields">
              <label className="field field--wide">地域<input name="region" defaultValue={settings.data.region} required pattern="[a-z0-9-]+" placeholder="ap-guangzhou" onChange={(event) => {
                if (event.currentTarget.value.trim() !== settings.data.region) setSelectedGroupId("");
                setGroups([]); setCapacity(null);
              }} /></label>
              <label className="field">SecretId<input name="secret_id" type="password" autoComplete="off" placeholder={settings.data.secret_id_hint ?? "输入 SecretId"} onChange={(event) => {
                if (event.currentTarget.value) setSelectedGroupId("");
                setGroups([]); setCapacity(null);
              }} /></label>
              <label className="field">SecretKey<input name="secret_key" type="password" autoComplete="new-password" placeholder={settings.data.credentials_configured ? "已配置，留空不修改" : "输入 SecretKey"} onChange={(event) => {
                if (event.currentTarget.value) setSelectedGroupId("");
                setGroups([]); setCapacity(null);
              }} /></label>
              <div className="autoscaling-discovery field--wide"><div><strong>第一步：验证连接</strong><small>使用上方地域和凭证读取伸缩组，不会保存配置或触发伸缩。</small></div><button className="button" type="button" disabled={discoverGroups.isPending} onClick={testConnection}><RefreshCw size={15} className={discoverGroups.isPending ? "spin" : ""} />{discoverGroups.isPending ? "正在连接…" : "测试连接并加载伸缩组"}</button></div>
              <label className="field field--wide">伸缩组<select name="auto_scaling_group_id" value={selectedGroupId} required disabled={!selectedGroupId && groups.length === 0} onChange={(event) => {
                const groupId = event.currentTarget.value;
                setSelectedGroupId(groupId);
                const group = groups.find((item) => item.auto_scaling_group_id === groupId);
                setCapacity(group ? groupToCapacity(group) : null);
              }}><option value="" disabled>{groups.length ? "请选择伸缩组" : "请先测试连接"}</option>{groups.length ? groups.map((group) => <option key={group.auto_scaling_group_id} value={group.auto_scaling_group_id}>{group.auto_scaling_group_name} · {group.auto_scaling_group_id}（{group.in_service_instance_count}/{group.desired_capacity} 实例）</option>) : settings.data.auto_scaling_group_id && <option value={settings.data.auto_scaling_group_id}>已配置 · {settings.data.auto_scaling_group_id}</option>}</select><small>{groups.length ? `已从腾讯云加载 ${groups.length} 个伸缩组` : settings.data.auto_scaling_group_id ? "当前显示已保存配置；重新测试连接可刷新伸缩组列表" : "连接通过后才能选择"}</small></label>
              <label className="field">计算资源池<input name="pool_id" defaultValue={settings.data.pool_id} required maxLength={128} /></label>
              <fieldset className="autoscaling-policy field--wide"><legend>自动伸缩规则</legend><p>扩容和缩容采用不同阈值，并要求持续满足条件，避免队列在边界附近反复抖动。启用前请关闭腾讯云中会修改期望实例数的告警策略和定时任务，保证只有一个容量控制器。</p><div>
                <label className="field">扩容队列阈值<input name="scale_out_queue_threshold" type="number" min={1} max={10_000_000} defaultValue={settings.data.scale_out_queue_threshold} required /></label>
                <label className="field">扩容持续时间（秒）<input name="scale_out_sustain_seconds" type="number" min={5} max={86_400} defaultValue={settings.data.scale_out_sustain_seconds} required /></label>
                <label className="field">每台承载积压任务（个）<input name="backlog_jobs_per_node" type="number" min={1} max={10_000_000} defaultValue={settings.data.backlog_jobs_per_node} required /><small>仅统计排队任务，扣除所有 READY 节点可立即承接的空闲并发。剩余排队量大于此值时，每次增加 1 台；等待节点 READY 后按最新队列重新判断。执行中任务不计入扩容需求，持续时间和冷却时间仍生效。</small></label>
                <label className="field">缩容队列阈值<input name="scale_in_queue_threshold" type="number" min={0} max={9_999_999} defaultValue={settings.data.scale_in_queue_threshold} required /></label>
                <label className="field">缩容持续时间（秒）<input name="scale_in_sustain_seconds" type="number" min={30} max={604_800} defaultValue={settings.data.scale_in_sustain_seconds} required /></label>
                <label className="field">动作冷却时间（秒）<input name="cooldown_seconds" type="number" min={30} max={86_400} defaultValue={settings.data.cooldown_seconds} required /></label>
                <label className="field">最少节点<input name="min_nodes" type="number" min={0} max={2_000} defaultValue={settings.data.min_nodes} required /></label>
                <label className="field">最多节点<input name="max_nodes" type="number" min={1} max={2_000} defaultValue={settings.data.max_nodes} required /></label>
                <label className="field">每节点 Worker 数<input name="workers_per_node" type="number" min={1} max={256} defaultValue={settings.data.workers_per_node} required /><small>现有云节点与后续 AS 新节点都会统一同步。</small></label>
                <label className="field">新节点 READY 超时（秒）<input name="node_ready_timeout_seconds" type="number" min={120} max={7_200} defaultValue={settings.data.node_ready_timeout_seconds} required /></label>
              </div></fieldset>
              <label className="check autoscaling-enable"><input name="enabled" type="checkbox" defaultChecked={settings.data.enabled} /><span><strong>启用自动扩容与自动缩容</strong><small>关闭后不创建新动作；已经进入云端执行的动作仍会完成或回滚。</small></span></label>
              <label className="field field--wide">变更原因<textarea name="reason" required minLength={3} maxLength={500} placeholder="例如：接入广州区生产伸缩组" /></label>
            </div>
            <div className="autoscaling-form__actions"><span>修订 {settings.data.version} · {formatTime(settings.data.updated_at)}</span><button className="button button--primary" type="submit" disabled={update.isPending || !selectedGroupId}><Save size={15} />{update.isPending ? "正在保存…" : "保存所选伸缩组"}</button></div>
          </form> : <p className="transcode-settings__readonly">当前账号只有查看权限。</p>}
        </section>

        <aside className="autoscaling-capacity">
          <p className="eyebrow">LIVE CAPACITY</p><h2>云侧容量</h2>
          {displayedCapacity ? <><strong>{displayedCapacity.current_instances}<small> 服务中实例</small></strong><dl><div><dt>最小</dt><dd>{displayedCapacity.min_size}</dd></div><div><dt>期望</dt><dd>{displayedCapacity.desired_capacity}</dd></div><div><dt>最大</dt><dd>{displayedCapacity.max_size}</dd></div><div><dt>IN_SERVICE</dt><dd>{displayedCapacity.in_service_instances}</dd></div></dl><p>{displayedCapacity.auto_scaling_group_name}<br />{displayedCapacity.status || "状态正常"}{displayedCapacity.in_activity_status ? ` · ${displayedCapacity.in_activity_status}` : ""}</p></> : <div className="autoscaling-capacity__empty"><Server size={20} /><p>测试连接并选择伸缩组后，在这里查看实时容量。</p></div>}
          {role === "ADMIN" && settings.data.credentials_configured && settings.data.auto_scaling_group_id && <form className="manual-scale-out" onSubmit={(event) => {
            event.preventDefault();
            const data = new FormData(event.currentTarget);
            scaleOut.mutate({ nodes: Number(data.get("nodes")), reason: String(data.get("reason")).trim() });
          }}><div><p className="eyebrow">MANUAL CAPACITY</p><h3>手动扩容</h3><small>复用 READY 验收与失败回滚，不受自动策略开关影响。</small></div><label className="field">增加节点数<input name="nodes" type="number" min={1} max={100} defaultValue={1} required /></label><label className="field">操作原因<input name="reason" minLength={3} maxLength={500} placeholder="例如：活动开始前预热容量" required /></label><button className="button button--primary" type="submit" disabled={scaleOut.isPending}><Server size={15} />{scaleOut.isPending ? "正在提交…" : "提交手动扩容"}</button></form>}
        </aside>
      </div>
    </>}

    <section className="section-block">
      <div className="section-heading"><div><p className="eyebrow">SAFE SCALE-IN</p><h2>伸缩组节点</h2></div><span className="section-heading__meta">缩容先停止接单，活动任务归零后才调用腾讯云</span></div>
      {!settings.data || nodes.isPending || capacityMembershipPending ? <LoadingRows columns={6} /> : poolNodes.length ? <div className="table-wrap"><table className="responsive-table autoscaling-node-table"><thead><tr><th>节点</th><th>实例</th><th>归属</th><th>状态</th><th>负载</th><th>操作</th></tr></thead><tbody>{poolNodes.map((node) => <tr key={node.node_id}><td data-label="节点"><strong>{node.name}</strong><small>{node.node_id}</small></td><td data-label="实例"><code>{node.provider_instance_id}</code></td><td data-label="归属"><span className={`capacity-owner capacity-owner--${node.scaling_mode.toLowerCase()}`}>{node.scaling_mode === "MANUAL" ? "人工保留" : "自动管理"}</span></td><td data-label="状态"><Status value={node.status} /></td><td data-label="负载"><strong>{node.active_attempts} 个任务</strong><small>{node.busy_slots} / {node.total_slots} Worker 忙碌</small></td><td data-label="操作" className="responsive-table__actions"><button className="button button--small button--danger" disabled={role !== "ADMIN" || !settings.data.credentials_configured || !settings.data.auto_scaling_group_id || !["READY", "DEGRADED"].includes(node.status)} onClick={() => setScaleTarget(node)}>安全释放</button></td></tr>)}</tbody></table></div> : <EmptyState title="伸缩组暂无在线节点">这里只展示当前腾讯云 AS 伸缩组实际关联的实例；固定节点请到“节点状态”查看。</EmptyState>}
    </section>

    <section className="section-block">
      <div className="section-heading"><div><p className="eyebrow">ACTION HISTORY</p><h2>伸缩动作</h2></div><span className="section-heading__meta">最近 100 条</span></div>
      {actions.isPending ? <LoadingRows columns={5} /> : actions.data?.length ? <div className="table-wrap"><table className="responsive-table"><thead><tr><th>目标</th><th>动作</th><th>状态</th><th>期望容量</th><th>时间 / 结果</th></tr></thead><tbody>{actions.data.map((action) => <tr key={action.action_id}><td data-label="目标"><strong>{action.node_name ?? "伸缩组容量"}</strong><small>{action.provider_instance_id ?? action.pool_id}</small></td><td data-label="动作"><strong>{action.action_type === "SCALE_OUT" ? action.requested_by === "system:autoscaler" ? "自动扩容" : "手动扩容" : action.requested_by === "system:autoscaler" ? "自动缩容" : "手动安全释放"}</strong><small>{action.activity_id ?? action.action_id}</small></td><td data-label="状态"><Status value={action.status} /></td><td data-label="期望容量">{action.desired_capacity_before == null || action.desired_capacity_target == null ? "—" : `${action.desired_capacity_before} → ${action.desired_capacity_target}`}</td><td data-label="时间 / 结果"><strong>{formatTime(action.updated_at)}</strong><small className={action.last_error ? "critical-text" : ""}>{action.last_error ?? action.reason}</small></td></tr>)}</tbody></table></div> : <EmptyState title="还没有伸缩动作">自动或手动扩容、排空、回滚和释放过程会记录在这里。</EmptyState>}
    </section>

    {scaleTarget && <Modal open onClose={() => setScaleTarget(null)} className="dialog--scale-in" label="排空并释放节点"><form onSubmit={(event) => {
      event.preventDefault();
      const data = new FormData(event.currentTarget);
      scaleIn.mutate({ node: scaleTarget, confirmation: String(data.get("confirmation")), reason: String(data.get("reason")) });
    }}><button type="button" className="icon-button dialog__close" aria-label="关闭" onClick={() => setScaleTarget(null)}><X size={18} /></button><p className="eyebrow"><AlertTriangle size={14} />DESTRUCTIVE CAPACITY CHANGE</p><h2>排空并释放节点</h2><p>控制面会立即停止该节点领取任务，任务归零后保护其他实例，并调用 ScaleInInstances。目标实例会被销毁，伸缩组期望容量同时减 1。</p><div className="dialog__target"><strong>{scaleTarget.name}</strong><code>{scaleTarget.provider_instance_id}</code></div><label>输入实例 ID 确认<input name="confirmation" required autoComplete="off" placeholder={scaleTarget.provider_instance_id ?? ""} /></label><label>操作原因<textarea name="reason" required minLength={3} maxLength={500} placeholder="例如：低峰期释放一台弹性节点" /></label><div className="dialog__actions"><button className="button" type="button" onClick={() => setScaleTarget(null)}>取消</button><button className="button button--danger" disabled={scaleIn.isPending}><Shield size={15} />{scaleIn.isPending ? "正在提交…" : "确认排空并缩容"}</button></div></form></Modal>}
  </div>;
}
